/* edgeRouting — pure edge geometry for the flow graphs: given the CURRENT
 * node positions (layout or dragged) and the raw edges, emit curved SVG
 * paths + label anchors + the canvas extent. dir 'h' = left→right ranks
 * (journey graph), dir 'v' = top→bottom ranks (workflow graph). Re-runs on
 * every drag, so it must stay cheap and side-effect free. */
import type {
    RawEdge
} from './journeyLayout';

/** Structural node contract — journey GNode and workflow WNode both fit. */
export interface RNode {
    key: string;
    kind: string;
    title: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface GEdge {
    key: string;
    from: string;
    to: string;
    label: string;
    kind?: string;
    path: string;
    lx: number;
    ly: number;
    tipTitle: string;
    tipBody: string;
}

export interface RoutedGraph {
    edges: GEdge[];
    width: number;
    height: number;
}

/** Compact edge label: the when expression with context noise stripped. */
function compactWhen(when?: string): string {
    if (!when) return 'default';
    const s = String(when)
        .replace(/\$\.context\./g, '')
        .replace(/\$\.steps\./g, '');
    return s.length > 30 ? `${s.slice(0, 28)}…` : s;
}

export function routeEdges(nodes: RNode[], raw: RawEdge[],
    dir: 'h' | 'v' = 'h'): RoutedGraph {
    const nodeAt = new Map(nodes.map((n) => [n.key, n]));
    const maxRight = Math.max(228, ...nodes.map((n) => n.x + n.w));
    const maxBottom = Math.max(104, ...nodes.map((n) => n.y + n.h));

    const groups = new Map<string, RawEdge[]>();
    for (const e of raw) {
        const k = `${e.from}→${e.to}`;
        groups.set(k, [...(groups.get(k) || []), e]);
    }

    const edges: GEdge[] = [];
    let backCount = 0;
    let sideMax = 0; // v-mode: rightmost extent of arcs/bows
    let edgeSeq = 0;
    groups.forEach((group) => {
        group.forEach((e, i) => {
            const a = nodeAt.get(e.from);
            const b = nodeAt.get(e.to);
            if (!a || !b) return;
            const off = (i - (group.length - 1) / 2) * 22;
            let path: string;
            let lx: number;
            let ly: number;
            if (a.key === b.key) {
                // Self loop — arc off the node's right edge.
                const r = 38 + i * 26;
                const sx = a.x + a.w;
                const sy = a.y + a.h * 0.3;
                const ey = a.y + a.h * 0.7;
                path = `M ${sx} ${sy} C ${sx + r} ${sy - r * 0.7}, `
                    + `${sx + r} ${ey + r * 0.7}, ${sx} ${ey}`;
                lx = sx + r * 0.82;
                ly = a.y + a.h / 2;
                sideMax = Math.max(sideMax, sx + r);
            } else if (dir === 'v' && b.x >= a.x + a.w + 20) {
                // Side edge (compensation) — right edge → left edge.
                const sx = a.x + a.w;
                const sy = a.y + a.h / 2 + off;
                const tx = b.x;
                const ty = b.y + b.h / 2 + off;
                const mx = (sx + tx) / 2;
                path = `M ${sx} ${sy} C ${mx} ${sy}, ${mx} ${ty}, ${tx} ${ty}`;
                lx = (sx + tx) / 2;
                ly = (sy + ty) / 2 - 8;
            } else if (dir === 'v' && b.y < a.y) {
                // Back edge (loop repeat) — local bow off the right edges.
                backCount += 1;
                const sx = a.x + a.w;
                const sy = a.y + a.h / 2;
                const tx = b.x + b.w;
                const ty = b.y + b.h / 2;
                const bx = Math.max(sx, tx) + 28 + i * 20;
                path = `M ${sx} ${sy} C ${bx} ${sy}, ${bx} ${ty}, ${tx} ${ty}`;
                lx = bx - 2;
                ly = (sy + ty) / 2;
                sideMax = Math.max(sideMax, bx + 8);
            } else if (dir === 'v') {
                // Forward vertical bezier — bottom center → top center.
                const sx = a.x + a.w / 2;
                const sy = a.y + a.h;
                const tx = b.x + b.w / 2;
                const ty = b.y;
                const my = (sy + ty) / 2;
                path = `M ${sx} ${sy} C ${sx + off} ${my}, `
                    + `${tx + off} ${my}, ${tx} ${ty}`;
                lx = (sx + tx) / 2 + off * 0.75;
                ly = my;
            } else if (b.x <= a.x) {
                // Back edge — bow routed under the whole grid.
                backCount += 1;
                const sx = a.x + a.w / 2;
                const sy = a.y + a.h;
                const tx = b.x + b.w / 2;
                const ty = b.y + b.h;
                const belowY = maxBottom + 36 + backCount * 18;
                path = `M ${sx} ${sy} C ${sx} ${belowY}, ${tx} ${belowY}, ${tx} ${ty}`;
                lx = (sx + tx) / 2;
                ly = (sy + ty) / 8 + belowY * 0.75;
            } else {
                // Forward bezier.
                const sx = a.x + a.w;
                const sy = a.y + a.h / 2;
                const tx = b.x;
                const ty = b.y + b.h / 2;
                const mx = (sx + tx) / 2;
                path = `M ${sx} ${sy} C ${mx} ${sy + off}, `
                    + `${mx} ${ty + off}, ${tx} ${ty}`;
                lx = (sx + tx + 6 * mx) / 8;
                ly = (sy + ty) / 2 + off * 0.75;
            }
            const toDesc = b.kind === 'end' ? 'END'
                : b.kind === 'journey' ? `journey ${b.title}` : b.key;
            edges.push({
                key: `e${edgeSeq++}`,
                from: e.from,
                to: e.to,
                label: e.label != null ? e.label : compactWhen(e.when),
                kind: e.kind,
                path,
                lx,
                ly,
                tipTitle: e.tipTitle || `${e.from} → ${toDesc}`,
                tipBody: e.tipBody || (e.when
                    ? `when: ${e.when}`
                    : 'default — taken when no other exit matches')
            });
        });
    });

    if (dir === 'v') {
        return {
            edges,
            width: Math.max(maxRight + 36, sideMax + 56),
            height: maxBottom + 30
        };
    }
    const width = maxRight + 98; // room for self-loop arcs + right padding
    const height = maxBottom + 28
        + (backCount > 0 ? 36 + backCount * 18 + 14 : 0);
    return {
        edges,
        width,
        height
    };
}
